import axios from "axios";
import { tokenStore, tenantStore } from "./tokenStore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    // iter61 — send + accept httpOnly auth cookies on every request.
    // Cookies are scoped ``Domain=.localappstore.in`` so they're shared
    // across ``bizildispatch.localappstore.in`` (frontend) +
    // ``bizildispatchapi.localappstore.in`` (backend). The Bearer header
    // interceptor below is still attached for backward compat with
    // existing localStorage tokens during the cutover window.
    withCredentials: true,
    // Android navigation bug fix (user-reported): with no timeout, a
    // request that the OS silently stalls — the Capacitor WebView's
    // network stack going through a backgrounding / Doze-mode
    // transition, or a WiFi↔cellular handoff — leaves its promise
    // pending FOREVER. Every "await api.get/post(...)" in this app sits
    // in front of either a `setLoading(false)` or a `nav(...)` call, so
    // a hung request means the loading spinner (or `RequireAuth`'s
    // FullPageLoader, which gates the ENTIRE authenticated app) never
    // clears and navigation never happens — exactly the "stuck on
    // loading screen, only a force-close fixes it" symptom. A finite
    // timeout guarantees the promise always settles, so callers' own
    // `.catch()`/`.finally()` handlers (already present almost
    // everywhere) run and recover normally instead of hanging.
    timeout: 20000,
});

// Hostnames the platform is served from. ANY hostname ending in one of
// these is treated as a "platform host" — its leading subdomain (if any)
// identifies the tenant; an empty/`www`/`app` leader resolves to the
// default tenant. The list intentionally covers both the legacy
// `bizil.app` apex and the new production root (`food.localappstore.in`).
// Self-hosted operators can declare their platform domain via
// ``REACT_APP_PLATFORM_HOST`` (e.g. ``bizildispatch.localappstore.in``)
// and BOTH the apex form (``acme.<platform_host>``) AND the bare
// platform host (which resolves to the default tenant) will work
// without any code edits.
const PLATFORM_HOST_ENV = (process.env.REACT_APP_PLATFORM_HOST || "").trim().toLowerCase();
// Frontend default tenant — operators with a self-hosted deployment can
// override the hardcoded ``bizil`` fallback by setting
// ``REACT_APP_DEFAULT_TENANT_SLUG=<their-slug>`` in their frontend .env
// at build time. This matches the backend's ``DEFAULT_TENANT_SLUG``
// env var so both sides agree on what "no explicit tenant" resolves to.
const DEFAULT_TENANT_SLUG = (process.env.REACT_APP_DEFAULT_TENANT_SLUG || "bizil").trim().toLowerCase() || "bizil";
const PLATFORM_APEX_HOSTS = [
    ".bizil.app",
    ".localappstore.in",
    ...(PLATFORM_HOST_ENV ? [`.${PLATFORM_HOST_ENV}`] : []),
];
// Hostnames whose FULL value resolves to the DEFAULT tenant (no
// subdomain extraction). The production landing page lives here.
// iter64 — ``marketplace.localappstore.in`` is the new platform parent
// domain. Both are accepted simultaneously so tenants can be migrated
// one at a time (driven by ``Tenant.marketplace_alias_domain``).
const PLATFORM_ROOT_HOSTS = new Set([
    "food.localappstore.in",
    "marketplace.localappstore.in",
    "bizil.app",
    "localappstore.in",
    ...(PLATFORM_HOST_ENV ? [PLATFORM_HOST_ENV] : []),
]);
// Subdomain leaders that should NOT be treated as a tenant slug —
// they're platform-level utility hosts.
const RESERVED_SUBS = new Set(["www", "api", "app", "food", "marketplace", "media", "static", "cdn"]);

/** Pull the tenant slug from the hostname when we recognise it.
 *  Returns:
 *    { slug: "bizil", source: "root" }      — platform root host
 *    { slug: "<sub>", source: "subdomain" } — tenant subdomain on a platform apex
 *    null                                    — unknown host (dev, custom_domain, preview)
 *
 *  Tenant subdomains are AUTHORITATIVE (override storage). The platform
 *  root host is NOT authoritative — the user might have come from a
 *  `?tenant=X` link and persisted that slug; we let it stick across
 *  SPA navigation until logout / `?tenant=bizil` reset.
 */
function tenantFromHost(host) {
    if (!host) return null;
    const h = host.toLowerCase();
    if (PLATFORM_ROOT_HOSTS.has(h)) return { slug: DEFAULT_TENANT_SLUG, source: "root" };
    for (const apex of PLATFORM_APEX_HOSTS) {
        if (h.endsWith(apex)) {
            const sub = h.slice(0, -apex.length).split(".")[0];
            if (!sub || RESERVED_SUBS.has(sub)) return { slug: DEFAULT_TENANT_SLUG, source: "root" };
            return { slug: sub, source: "subdomain" };
        }
    }
    return null;
}

// Resolve tenant slug in this order:
//   1. Impersonation session (super-admin "preview as tenant")
//   2. Tenant SUBDOMAIN in host (authoritative — overrides storage)
//   3. ``?tenant=X`` query param (persists to sessionStorage — iter146,
//      was localStorage, which leaked the active tenant across every
//      other open tab on the same origin)
//   4. Persisted sessionStorage slug (sticky within this tab only,
//      across SPA navigation)
//   5. Platform root host → ``"bizil"`` default
//   6. Unknown host → ``"bizil"`` default
//
// The persisted slug is the tab's tenant context. Nothing but an explicit
// address change replaces it: signing out, a session that belongs to
// another tenant and a tenant that has become unavailable all leave it
// alone (iter179 — each of those used to erase it, and the next lookup
// then landed on the default tenant). To move to another tenant, open
// its link (``?tenant=<slug>``) or its own domain.
export function resolveTenantSlug() {
    if (typeof window === "undefined") return DEFAULT_TENANT_SLUG;
    // 1. Per-tab impersonation tenant always wins.
    const sessionSlug = tenantStore.get && window.sessionStorage.getItem("bizil_tenant_slug");
    if (sessionSlug && tokenStore.isImpersonation()) return sessionSlug;

    const host = window.location.hostname;
    const hostRes = tenantFromHost(host);

    // 2. Tenant SUBDOMAIN on a recognised apex → authoritative.
    if (hostRes && hostRes.source === "subdomain") {
        tenantStore.setPersistent(hostRes.slug);
        return hostRes.slug;
    }

    // 3. Explicit ``?tenant=X`` override — persist and use.
    //    Exception: on ``/legal/*`` paths the override is **transient**
    //    so a Play Store / App Store reviewer landing on tenant-X's
    //    legal page doesn't permanently rewire the browser's persisted
    //    tenant. The LegalPage already passes an explicit
    //    X-Tenant-Slug header for its own API calls; this guard simply
    //    prevents storage pollution.
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("tenant");
    if (fromQuery) {
        const isLegalPath = window.location.pathname.startsWith("/legal/");
        if (!tokenStore.isImpersonation() && !isLegalPath) {
            tenantStore.setPersistent(fromQuery);
        }
        return fromQuery;
    }

    // 4. Sticky persisted slug (user came via ?tenant=X earlier).
    const fromStorage = tenantStore.get();
    if (fromStorage) return fromStorage;

    // 5. Platform root host with no override → default tenant.
    if (hostRes && hostRes.source === "root") return DEFAULT_TENANT_SLUG;

    // 6. Unknown host (dev / preview / unrecognised custom_domain).
    return DEFAULT_TENANT_SLUG;
}

// ---- Custom-domain resolution -----------------------------------------
//
// An unrecognised host (step 6 above) isn't necessarily "unknown" — it's
// exactly what a tenant-owned custom domain looks like from the client's
// point of view (no ``<slug>.<platform-apex>`` structure to parse). We ask
// the backend which tenant owns this hostname by passing it as a ``?host=``
// query param on the FIXED ``REACT_APP_BACKEND_URL`` — the same absolute
// URL every other call in this file already uses, cross-origin. This is
// deliberately NOT a same-origin/relative call: a same-origin call would
// only work if the custom domain's own server reverse-proxies ``/api`` to
// the backend, which most custom-domain deployments won't do (they just
// serve the static build). Passing the hostname explicitly means a plain
// static file host is enough — see ``tenant_router.py:_resolve_slug_from_request``
// step 4 for why trusting a client-supplied hostname here is safe: this
// endpoint is public/unauthenticated either way (same data as
// ``/tenant/by-slug/{slug}``, which already accepts any slug from anyone).
let _customDomainResolveAttempted = false;

function _shouldAttemptCustomDomainResolve() {
    if (typeof window === "undefined") return false;
    if (tokenStore.isImpersonation()) return false; // explicit tenant already pinned
    if (tenantFromHost(window.location.hostname)) return false; // recognised platform host
    const params = new URLSearchParams(window.location.search);
    if (params.get("tenant")) return false; // explicit ?tenant= override
    if (tenantStore.get()) return false; // already resolved earlier this tab
    return true;
}

/** Best-effort, one-shot-per-tab lookup of the tenant slug for an
 *  unrecognised host, via the platform's fixed backend URL passing the
 *  current hostname explicitly. No-ops (fast) for recognised platform
 *  hosts, or when a slug is already known some other way. Awaited once
 *  by ``TenantProvider`` before it computes the slug for its first
 *  ``/tenant/by-slug`` call — everything downstream (including the
 *  request interceptor below) keeps reading ``resolveTenantSlug()``
 *  as before, which picks up the persisted slug via its existing
 *  "sticky per-tab" step.
 */
export async function ensureCustomDomainTenantResolved() {
    if (_customDomainResolveAttempted) return;
    _customDomainResolveAttempted = true;
    if (!_shouldAttemptCustomDomainResolve()) return;
    try {
        const { data } = await axios.get(`${API}/tenant/resolve`, {
            params: { host: window.location.hostname },
            timeout: 6000,
        });
        if (data && typeof data.slug === "string" && data.slug) {
            tenantStore.setPersistent(data.slug);
        }
    } catch {
        // Backend unreachable, or no ``custom_domain`` match — fall
        // through to the existing subdomain/default resolution untouched.
    }
}

// ---- Impersonation boot ----------------------------------------------------
// If the URL carries ?impersonate=TOKEN&tenant=SLUG (set by Super Admin
// "Preview as tenant"), park the token in **sessionStorage** so this tab only
// is logged in as the tenant admin — the original tab keeps its super admin
// session intact.
if (typeof window !== "undefined") {
    const p = new URLSearchParams(window.location.search);
    const imp = p.get("impersonate");
    if (imp) {
        tokenStore.set(imp, { sessionOnly: true });
        const slug = p.get("tenant");
        if (slug) tenantStore.setSession(slug);
        p.delete("impersonate");
        const qs = p.toString();
        window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
    }
}

api.interceptors.request.use((config) => {
    config.headers["X-Tenant-Slug"] = resolveTenantSlug();
    const token = tokenStore.get();
    if (token) config.headers["Authorization"] = `Bearer ${token}`;
    return config;
});

// iter61 — auto-refresh access cookie on 401.
//
// When the access cookie expires the next API call returns 401. We try
// ``POST /auth/refresh`` (which uses the refresh cookie scoped to that
// path) once and replay the original request. If refresh also fails
// the user is genuinely logged out and we clear localStorage + reject.
//
// Guards:
//  • Single retry per request (``_retried`` flag) — no infinite loop
//    if /auth/refresh itself starts 401-ing.
//  • In-flight de-dupe: if N requests fail at once we only fire ONE
//    refresh and the others await its result.
//  • Refresh endpoint itself bypassed (no retry on its own 401).
// Outcomes of a refresh attempt. The distinction matters: this used to be a bare boolean, so a timeout, a DNS
// failure or a 502 was indistinguishable from "your refresh credential is expired" — and the caller below
// destroyed the stored token either way. That is the unexpected-logout bug: a customer whose access cookie had
// expired, returning to the app mid Wi-Fi↔mobile handoff, lost a perfectly valid 30-day session and had to
// request a fresh OTP.
export const REFRESH_OK = "REFRESHED";
export const REFRESH_REJECTED = "REJECTED";          // the endpoint answered: this credential is no good
export const REFRESH_UNREACHABLE = "UNREACHABLE";    // we never got an answer — says nothing about the session

let _refreshInFlight = null;
async function _runRefresh() {
    if (!_refreshInFlight) {
        _refreshInFlight = (async () => {
            try {
                // Bare axios call (bypasses the `api` instance's interceptors
                // on purpose, to avoid recursing back into this same refresh
                // logic) — so it needs its OWN timeout too, for the same
                // reason `api` itself does (see the `timeout` comment above).
                //
                // iter179 — it bypasses the tenant header too, so name the tenant here. The refresh cookie is
                // shared by every tenant's storefront in this browser; with the tenant named, the backend never
                // renews another store's session on behalf of this tab (it answers 401 and leaves that cookie
                // alone), and this tab is simply signed out here.
                await axios.post(`${API}/auth/refresh`, null, {
                    withCredentials: true,
                    timeout: 20000,
                    headers: { "X-Tenant-Slug": resolveTenantSlug() },
                });
                return REFRESH_OK;
            } catch (e) {
                // Only the endpoint itself saying 401/403 proves the refresh credential is dead. A 5xx, a
                // gateway error, a timeout or no response at all means we couldn't ask — the session may well
                // still be valid, and the next attempt should find out rather than the user being logged out.
                const st = e?.response?.status;
                return st === 401 || st === 403 ? REFRESH_REJECTED : REFRESH_UNREACHABLE;
            } finally {
                // Tiny delay so concurrent failing requests piggyback
                // onto this promise before it resets.
                setTimeout(() => { _refreshInFlight = null; }, 50);
            }
        })();
    }
    return _refreshInFlight;
}
api.interceptors.response.use(
    (r) => r,
    async (err) => {
        const cfg = err?.config;
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;

        // The tenant this tab is on no longer exists (deleted or renamed
        // while the tab was open, or a stale ``?tenant=`` link). Every
        // tenant-scoped call then returns 404 "Tenant not found" and
        // pages silently hang on "Loading…" states.
        //
        // iter179 — reload ONCE so the boot lookup runs again and the
        // app shows its "Tenant Not Found" screen for THIS tenant. The
        // stored slug is deliberately kept: this used to erase it and
        // reload, which opened the default tenant instead — the visitor
        // was moved to another store with no message at all. Leaving for
        // the home page is now the visitor's own choice (the button on
        // that screen).
        //
        // The boot lookup itself is skipped: ``TenantProvider`` already
        // turns its 404 into that screen, no reload needed.
        //
        // Loop guard MUST survive ``location.reload()`` — an in-memory
        // flag would reset on reload. ``sessionStorage`` lives across
        // reloads but dies when the tab closes, which is exactly the
        // lifetime we want.
        if (status === 404 && typeof detail === "string"
            && /Tenant.*not found/i.test(detail)) {
            const isTenantLookup = /\/tenant\/(by-slug|resolve)(\/|\?|$)/.test(cfg?.url || "");
            const alreadyTried = typeof window !== "undefined"
                && window.sessionStorage.getItem("bizil_stale_tenant_healed") === "1";
            const hasStoredSlug = !!(tenantStore.get && tenantStore.get());
            if (!isTenantLookup && !alreadyTried && hasStoredSlug) {
                try {
                    window.sessionStorage.setItem("bizil_stale_tenant_healed", "1");
                } catch {/* ignore */ }
                window.location.reload();
                return new Promise(() => {/* never resolves — we're reloading */ });
            }
            // Already reloaded once in this tab, or it was the boot
            // lookup, or there was no stored slug — let the 404 surface
            // naturally instead of looping.
        }

        // Only retry on 401 + only once + skip the refresh endpoint itself
        if (
            status === 401 && cfg && !cfg._retried
            && !(cfg.url || "").endsWith("/auth/refresh")
        ) {
            cfg._retried = true;
            const outcome = await _runRefresh();
            if (outcome === REFRESH_OK) {
                // Replay — interceptors fire again, but ``_retried`` is
                // already set so we won't loop.
                return api.request(cfg);
            }
            if (outcome === REFRESH_REJECTED) {
                // Refresh credential is genuinely dead → truly logged out, drop the legacy
                // localStorage Bearer too so the next page load doesn't
                // attempt to re-use a dead JWT.
                tokenStore.clear();
            } else {
                // We never reached /auth/refresh. The 401 this rejects with is NOT evidence the session is
                // over — we didn't get to ask. Flag it so AuthContext doesn't read it as one and sign the
                // user out; without this the 401 alone would still end the session on the next resume.
                err.sessionCheckUnreachable = true;
            }
        }
        return Promise.reject(err);
    },
);

export function inr(n) {
    if (n == null) return "\u20B90";
    return `\u20B9${Math.round(Number(n)).toLocaleString("en-IN")}`;
}

export function inr2(n) {
    if (n == null) return "\u20B90";
    return `\u20B9${Number(n).toFixed(2)}`;
}
